import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Maps timezone identifiers to their UTC offsets and abbreviations
 * Note: Etc/GMT+X means UTC-X (opposite sign)
 */
const TIMEZONE_INFO: Record<string, { offset: number; abbr: string }> = {
  // Common North American timezones (Daylight Saving Time)
  'America/Halifax': { offset: -3, abbr: 'ADT' }, // Atlantic Daylight Time (UTC-3)
  'America/New_York': { offset: -4, abbr: 'EDT' }, // Eastern Daylight Time (UTC-4)
  'America/Chicago': { offset: -5, abbr: 'CDT' }, // Central Daylight Time (UTC-5)
  'America/Denver': { offset: -6, abbr: 'MDT' }, // Mountain Daylight Time (UTC-6)
  'America/Los_Angeles': { offset: -7, abbr: 'PDT' }, // Pacific Daylight Time (UTC-7)
  'America/Caracas': { offset: -4, abbr: 'VET' }, // Venezuela Time (UTC-4)
  'America/Sao_Paulo': { offset: -3, abbr: 'BRT' }, // Brasilia Time (UTC-3)
  'America/Noronha': { offset: -2, abbr: 'FNT' }, // Fernando de Noronha Time (UTC-2)

  // Winter time equivalents (Standard Time)
  'America/Halifax_ST': { offset: -4, abbr: 'AST' }, // Atlantic Standard Time (UTC-4)
  'America/New_York_ST': { offset: -5, abbr: 'EST' }, // Eastern Standard Time (UTC-5)
  'America/Chicago_ST': { offset: -6, abbr: 'CST' }, // Central Standard Time (UTC-6)
  'America/Denver_ST': { offset: -7, abbr: 'MST' }, // Mountain Standard Time (UTC-7)
  'America/Los_Angeles_ST': { offset: -8, abbr: 'PST' }, // Pacific Standard Time (UTC-8)

  // Pacific timezones
  'Pacific/Auckland': { offset: 12, abbr: 'NZST' }, // New Zealand Standard Time (UTC+12)
  'Pacific/Midway': { offset: -11, abbr: 'SST' }, // Samoa Standard Time (UTC-11)
  'Pacific/Honolulu': { offset: -10, abbr: 'HST' }, // Hawaii Standard Time (UTC-10)
  'Pacific/Gambier': { offset: -9, abbr: 'GAMT' }, // Gambier Time (UTC-9)
  'Pacific/Noumea': { offset: 11, abbr: 'NCT' }, // New Caledonia Time (UTC+11)
  'Pacific/Fiji': { offset: 12, abbr: 'FJT' }, // Fiji Time (UTC+12)

  // European timezones
  'Europe/London': { offset: 1, abbr: 'BST' }, // British Summer Time (UTC+1)
  'Europe/Berlin': { offset: 2, abbr: 'CEST' }, // Central European Summer Time (UTC+2)
  'Europe/Paris': { offset: 2, abbr: 'CEST' }, // Central European Summer Time (UTC+2)
  'Europe/Amsterdam': { offset: 2, abbr: 'CEST' }, // Central European Summer Time (UTC+2)
  'Europe/Rome': { offset: 2, abbr: 'CEST' }, // Central European Summer Time (UTC+2)
  'Europe/Madrid': { offset: 2, abbr: 'CEST' }, // Central European Summer Time (UTC+2)
  'Europe/Bucharest': { offset: 3, abbr: 'EEST' }, // Eastern European Summer Time (UTC+3)
  'Europe/Helsinki': { offset: 3, abbr: 'EEST' }, // Eastern European Summer Time (UTC+3)
  'Europe/Athens': { offset: 3, abbr: 'EEST' }, // Eastern European Summer Time (UTC+3)
  'Europe/Kiev': { offset: 3, abbr: 'EEST' }, // Eastern European Summer Time (UTC+3)
  'Europe/Moscow': { offset: 3, abbr: 'MSK' }, // Moscow Time (UTC+3)
  'Atlantic/Azores': { offset: -1, abbr: 'AZOT' }, // Azores Time (UTC-1)

  // Asian timezones
  'Asia/Dubai': { offset: 4, abbr: 'GST' }, // Gulf Standard Time (UTC+4)
  'Asia/Karachi': { offset: 5, abbr: 'PKT' }, // Pakistan Standard Time (UTC+5)
  'Asia/Dhaka': { offset: 6, abbr: 'BST' }, // Bangladesh Standard Time (UTC+6)
  'Asia/Bangkok': { offset: 7, abbr: 'ICT' }, // Indochina Time (UTC+7)
  'Asia/Shanghai': { offset: 8, abbr: 'CST' }, // China Standard Time (UTC+8)
  'Asia/Tokyo': { offset: 9, abbr: 'JST' }, // Japan Standard Time (UTC+9)

  // Australian timezones
  'Australia/Sydney': { offset: 10, abbr: 'AEST' }, // Australian Eastern Standard Time (UTC+10)

  // UTC and GMT
  UTC: { offset: 0, abbr: 'UTC' },
  GMT: { offset: 0, abbr: 'GMT' },
  'Etc/UTC': { offset: 0, abbr: 'UTC' },
  'Etc/GMT': { offset: 0, abbr: 'GMT' },

  // Etc/GMT+X mappings (note: Etc/GMT+X means UTC-X)
  'Etc/GMT+12': { offset: -12, abbr: 'GMT-12' },
  'Etc/GMT+11': { offset: -11, abbr: 'GMT-11' },
  'Etc/GMT+10': { offset: -10, abbr: 'GMT-10' },
  'Etc/GMT+9': { offset: -9, abbr: 'GMT-9' },
  'Etc/GMT+8': { offset: -8, abbr: 'GMT-8' },
  'Etc/GMT+7': { offset: -7, abbr: 'GMT-7' },
  'Etc/GMT+6': { offset: -6, abbr: 'GMT-6' },
  'Etc/GMT+5': { offset: -5, abbr: 'GMT-5' },
  'Etc/GMT+4': { offset: -4, abbr: 'GMT-4' },
  'Etc/GMT+3': { offset: -3, abbr: 'GMT-3' },
  'Etc/GMT+2': { offset: -2, abbr: 'GMT-2' },
  'Etc/GMT+1': { offset: -1, abbr: 'GMT-1' },
  'Etc/GMT-1': { offset: 1, abbr: 'GMT+1' },
  'Etc/GMT-2': { offset: 2, abbr: 'GMT+2' },
  'Etc/GMT-3': { offset: 3, abbr: 'GMT+3' },
  'Etc/GMT-4': { offset: 4, abbr: 'GMT+4' },
  'Etc/GMT-5': { offset: 5, abbr: 'GMT+5' },
  'Etc/GMT-6': { offset: 6, abbr: 'GMT+6' },
  'Etc/GMT-7': { offset: 7, abbr: 'GMT+7' },
  'Etc/GMT-8': { offset: 8, abbr: 'GMT+8' },
  'Etc/GMT-9': { offset: 9, abbr: 'GMT+9' },
  'Etc/GMT-10': { offset: 10, abbr: 'GMT+10' },
  'Etc/GMT-11': { offset: 11, abbr: 'GMT+11' },
  'Etc/GMT-12': { offset: 12, abbr: 'GMT+12' },
};

/**
 * Gets timezone info for a given timezone string
 */
export function getTimezoneInfo(timezoneString: string): { offset: number; abbr: string } {
  return TIMEZONE_INFO[timezoneString] || { offset: 0, abbr: 'UTC' };
}

/**
 * Validates if a timezone identifier is supported
 */
export function isValidTimezone(timezoneString: string): boolean {
  return timezoneString in TIMEZONE_INFO;
}

/**
 * Convert a UTC datetime string to a specific timezone
 * Returns a dayjs object in the specified timezone
 */
export function fromJsonToTimezone(
  dateString: string | null,
  timezoneString: string | null,
): dayjs.Dayjs | null {
  if (!dateString || !timezoneString) return null;

  try {
    // Parse the UTC datetime
    const utcDateTime = dayjs.utc(dateString);

    // Get timezone info
    const timezoneInfo = getTimezoneInfo(timezoneString);

    // Apply the timezone offset
    const localDateTime = utcDateTime.utcOffset(timezoneInfo.offset * 60);

    return localDateTime;
  } catch (e) {
    console.error('Timezone conversion failed:', {
      dateString,
      timezoneString,
      error: e,
    });
    // Fallback to UTC
    if (dateString) {
      try {
        return dayjs.utc(dateString);
      } catch {
        return null;
      }
    }
    return null;
  }
}

/**
 * Convert a datetime string in a specific timezone to local timezone
 */
export function fromJsonWithTimezone(
  dateString: string | null,
  timezoneString: string | null,
): dayjs.Dayjs | null {
  if (!dateString) return null;

  try {
    // Check if the date string already contains timezone information
    if (dateString.endsWith('Z')
        || dateString.includes('+')
        || (dateString.includes('T') && dateString.length > 19)) {
      // Date string already has timezone information, parse it directly
      return dayjs(dateString);
    }

    // If no timezone string provided, fallback to basic parsing
    if (!timezoneString) {
      return dayjs(dateString);
    }

    // Get timezone info
    const timezoneInfo = getTimezoneInfo(timezoneString);

    // Parse the datetime and apply timezone offset
    const parsedDateTime = dayjs(dateString);
    const localDateTime = parsedDateTime.utcOffset(timezoneInfo.offset * 60);

    return localDateTime;
  } catch (e) {
    console.error('Timezone conversion failed:', {
      dateString,
      timezoneString,
      error: e,
    });
    // Fallback to parsing as local time
    return dayjs(dateString);
  }
}

/**
 * Get day suffix (1st, 2nd, 3rd, 4th, etc.)
 */
export function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Format a datetime with timezone to display format
 * Example: "Sep 24th 12:00 PM ADT"
 */
export function formatDateTimeWithTimezone(
  dateString: string | null,
  timezoneString: string | null,
): string | null {
  const tzDateTime = fromJsonToTimezone(dateString, timezoneString);
  if (!tzDateTime) return null;

  const day = tzDateTime.date();
  const suffix = getDaySuffix(day);
  const monthName = tzDateTime.format('MMM');
  const time = tzDateTime.format('h:mm A');

  // Get timezone abbreviation
  const timezoneInfo = getTimezoneInfo(timezoneString || '');
  const tzAbbr = timezoneInfo.abbr;

  return `${monthName} ${day}${suffix} ${time} ${tzAbbr}`;
}

/**
 * Format a date to display format
 * Example: "Sep 24th, 2024"
 */
export function formatDate(dateString: string | null): string | null {
  if (!dateString) return null;

  try {
    const date = dayjs(dateString);
    const day = date.date();
    const suffix = getDaySuffix(day);
    const monthName = date.format('MMM');
    const year = date.format('YYYY');

    return `${monthName} ${day}${suffix}, ${year}`;
  } catch (e) {
    console.error('Date formatting failed:', { dateString, error: e });
    return null;
  }
}

/**
 * Format a date to display time only
 * Example: "12:00 PM"
 */
export function formatTime(dateString: string | null): string | null {
  if (!dateString) return null;

  try {
    const date = dayjs(dateString);
    return date.format('h:mm A');
  } catch (e) {
    console.error('Time formatting failed:', { dateString, error: e });
    return null;
  }
}
